import type { InputHTMLAttributes, Ref } from "react";

import { cx } from "@/lib/cx";
import { Button } from "./Button";
import { control } from "./control";
import { CloseIcon } from "@/ui/icons";

/**
 * The iOS search field — `IOS-DESIGN.md` §5.7.
 *
 * Its own component rather than a `TextField` variant, for three reasons that
 * are all about it *not* being a form field:
 *
 * 1. **It has no visible label and it must not have one.** A search field is
 *    identified by its magnifier and its placeholder; a caption above it is the
 *    web pattern. The accessible name comes from `aria-label`, and
 *    `role="searchbox"` is what `type="search"` already gives it.
 * 2. **It is the 40px default rung.** Search and form controls now share the
 *    compact ladder, so a search field aligns with the button beside it.
 * 3. **Clearing is not optional.** Every iOS search field can be emptied in one
 *    tap; a `TextField` only grows a ✕ when the caller asks.
 *
 * The filled skin comes from `control()`, so search shares the same field
 * material and legal edge as every form control. The plain global-search skin
 * remains borderless and fill-less. The global accent `:focus-visible` outline
 * is still the focus state (`index.css`).
 */

export type SearchFieldProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "type" | "value" | "onChange" | "size"
> & {
  value: string;
  onValueChange: (next: string) => void;
  /** The accessible name, e.g. `Search tasks`. */
  label: string;
  placeholder?: string;
  /**
   * The field's skin.
   *
   * - `fill` (default) — the shared paper/well field and its 3:1 edge.
   * - `plain` — no fill and no border. For the global search bar, whose shell
   *   already supplies the one boundary beneath the strip.
   */
  variant?: "fill" | "plain";
  ref?: Ref<HTMLInputElement>;
  /** Applied to the wrapper. Use `inputClassName` for the input itself. */
  className?: string;
  inputClassName?: string;
};

export function SearchField({
  value,
  onValueChange,
  label,
  placeholder = "Search",
  variant = "fill",
  className,
  inputClassName,
  ref,
  ...rest
}: SearchFieldProps) {
  return (
    <div className={cx("relative flex items-center", className)}>
      <MagnifierIcon />

      <input
        ref={ref}
        type="search"
        value={value}
        aria-label={label}
        placeholder={placeholder}
        onChange={(event) => onValueChange(event.target.value)}
        className={control({
          size: "md",
          variant: variant === "plain" ? "plain" : "filled",
          leading: true,
          trailing: value !== "",
          className: cx(
            // `plain` normally has no gutter because it is a grouped row. This
            // plain search still owns overlaid magnifier/clear controls, so it
            // explicitly reserves their space without restoring a skin.
            variant === "plain" && (value === "" ? "pl-8 pr-3" : "pl-8 pr-8"),
            // The native ✕ that WebKit draws for `type="search"` is the one
            // piece of system chrome that would survive into an otherwise
            // designed control — the same class of defect as `UI-REVIEW.md` P14.
            "[&::-webkit-search-cancel-button]:appearance-none",
            inputClassName,
          ),
        })}
        {...rest}
      />

      {value === "" ? null : (
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          onClick={() => onValueChange("")}
          aria-label={`Clear ${label.toLowerCase()}`}
          className="absolute! right-2"
        >
          <CloseIcon className="size-4" />
        </Button>
      )}
    </div>
  );
}

/**
 * Drawn here rather than in `overlay/icons` because it belongs to this control
 * and nothing else asks for it yet. It uses the neutral muted mark colour so
 * it remains legible on the field's fill without competing with the value.
 */
function MagnifierIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      aria-hidden
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      className="pointer-events-none absolute left-3 size-4 text-muted"
    >
      <circle cx="9" cy="9" r="5.2" />
      <path d="m13 13 3.4 3.4" />
    </svg>
  );
}
